import { useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export interface SearchableMultiSelectOption {
  value: string;
  label: string;
  sublabel?: string;
  disabled?: boolean;
}

interface SearchableMultiSelectProps {
  selected: string[];
  onToggle: (value: string) => void;
  options: SearchableMultiSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  popoverClassName?: string;
  maxInlineDisplay?: number;
  label?: ReactNode;
}

function formatTriggerText(
  selected: string[],
  options: SearchableMultiSelectOption[],
  placeholder: string,
  maxInline: number,
): string {
  if (selected.length === 0) return placeholder;
  if (selected.length <= maxInline) {
    return selected
      .map((v) => options.find((o) => o.value === v)?.label ?? v)
      .join(', ');
  }
  return `${selected.length} selected`;
}

export function SearchableMultiSelect({
  selected,
  onToggle,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  className,
  popoverClassName,
  maxInlineDisplay = 2,
  label,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const triggerText = formatTriggerText(selected, options, placeholder, maxInlineDisplay);

  return (
    <div className="space-y-1.5">
      {label && (
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={open}
            className={cn('h-9 w-full justify-between font-normal', className)}
          >
            <span className="truncate">{triggerText}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          style={{ width: 'min(92vw, 380px)' }}
          className={cn('p-0', popoverClassName)}
          align="start"
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.sublabel ? `${option.label} ${option.sublabel}` : option.label}
                      disabled={option.disabled}
                      onSelect={() => {
                        if (!option.disabled) onToggle(option.value);
                      }}
                    >
                      <div
                        className={cn(
                          'mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-muted-foreground/30',
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="whitespace-normal break-words leading-snug">
                          {option.label}
                        </div>
                        {option.sublabel && (
                          <div className="whitespace-normal break-words text-xs text-muted-foreground leading-snug">
                            {option.sublabel}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
